import React, { useState, useEffect } from "react";
import axios from "axios";
import { API_BASE_URL } from "../config";
import {
  LayoutDashboard, UtensilsCrossed, Menu as MenuIcon,
  ReceiptText, FileBarChart, Settings as SettingsIcon, LogOut,
  IndianRupee, Receipt, BarChart3, ChevronDown, X, Calendar
} from "lucide-react";
import { ChevronRight, ChevronLeft } from "lucide-react";
import logo from "../assets/lg.png";
import { useNavigate } from "react-router-dom";
import "./Reports.css";

function Reports() {
  const navigate = useNavigate();
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [viewMode, setViewMode] = useState("daily");
  const [allSales, setAllSales] = useState([]); 
  const [isSidebarOpen, setIsSidebarOpen] = useState(false); // Mobile sidebar state
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [reportData, setReportData] = useState({
    totalSales: 0,
    gstSales: 0,
    nonGstSales: 0,
    orders: []
  });

  const fetchSales = async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/reports.php`);
      setAllSales(res.data);
    } catch (err) {
      console.error("Error fetching sales history");
    }
  };

  useEffect(() => {
    fetchSales();
  }, []);

  useEffect(() => {
    const today = new Date(selectedDate);
    
    const filteredByDate = allSales.filter(order => {
      const orderDate = new Date(order.sale_date);
      if (viewMode === "daily") return order.sale_date === selectedDate;
      if (viewMode === "weekly") {
        const day = today.getDay();
        const diff = today.getDate() - day + (day === 0 ? -6 : 1);
        const monday = new Date(new Date(selectedDate).setDate(diff));
        monday.setHours(0,0,0,0);
        const nextMonday = new Date(monday);
        nextMonday.setDate(monday.getDate() + 7);
        return orderDate >= monday && orderDate < nextMonday;
      }
      if (viewMode === "monthly") {
        return orderDate.getMonth() === today.getMonth() && 
               orderDate.getFullYear() === today.getFullYear();
      }
      if (viewMode === "yearly") {
        return orderDate.getFullYear() === today.getFullYear();
      }
      return false;
    });

    const formattedOrders = filteredByDate.map((bill) => {
      const sameCategorySales = allSales.filter(s => s.table_id === bill.table_id);
      const categorySpecificIndex = sameCategorySales.length - sameCategorySales.indexOf(bill);
      const seq = categorySpecificIndex.toString().padStart(4, '0');
      
      const displayId = bill.table_id === "Walk-in" 
        ? `S-WALK-${seq}` 
        : `T${bill.table_id}-${seq}`;
      
      return { 
          ...bill, 
          displayId 
      };
    });

    let total = 0, gstSum = 0, nonGstSum = 0;
    formattedOrders.forEach(order => {
      const amount = parseFloat(order.grand_total);
      total += amount;
      if (parseFloat(order.gst_amount) > 0) gstSum += amount;
      else nonGstSum += amount;
    });

    setReportData({ 
      totalSales: total, 
      gstSales: gstSum, 
      nonGstSales: nonGstSum, 
      orders: formattedOrders 
    });
  }, [selectedDate, viewMode, allSales]);

  return (
    <div className="dash-container">
      {/* SIDEBAR WITH MOBILE DRAWER LOGIC */}
      <aside className={`sidebar ${isSidebarOpen ? 'active' : ''} ${isSidebarCollapsed ? 'collapsed' : ''}`}>

  {/* COLLAPSE BUTTON */}
  <button 
    className="sidebar-toggle-btn d-none d-md-flex"
    onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
  >
    {isSidebarCollapsed ? <ChevronRight size={18} /> : <ChevronLeft size={18} />}
  </button>

  <div className="sidebar-top">

    {/* ✅ LOGO (same as Dashboard) */}
    <div className="brand">
      <img src={logo} alt="Logo" className="sidebar-logo" />

      {/* Mobile Close */}
      <X 
        className="d-md-none ms-auto cursor-pointer" 
        onClick={() => setIsSidebarOpen(false)} 
      />
    </div>

    {/* NAVIGATION */}
    <nav className="nav-menu">
      <div className="nav-item" onClick={() => navigate("/dashboard")}>
        <LayoutDashboard size={20} /> {!isSidebarCollapsed && "Dashboard"}
      </div>

      <div className="nav-item" onClick={() => navigate("/tables")}>
        <UtensilsCrossed size={20} /> {!isSidebarCollapsed && "Tables"}
      </div>

      <div className="nav-item" onClick={() => navigate("/menu")}>
        <MenuIcon size={20} /> {!isSidebarCollapsed && "Menu"}
      </div>

      <div className="nav-item" onClick={() => navigate("/billing")}>
        <ReceiptText size={20} /> {!isSidebarCollapsed && "Billing"}
      </div>

      {/* ✅ ACTIVE PAGE */}
      <div className="nav-item active">
        <FileBarChart size={20} /> {!isSidebarCollapsed && "Reports"}
      </div>
    </nav>
  </div>

  {/* BOTTOM */}
  <div className="sidebar-bottom">
    <div className="nav-item" onClick={() => navigate("/settings")}>
      <SettingsIcon size={20} /> {!isSidebarCollapsed && "Settings"}
    </div>

    <div className="nav-item logout-red" onClick={() => navigate("/")}>
      <LogOut size={20} /> {!isSidebarCollapsed && "Logout"}
    </div>
  </div>

</aside>

      <div className="main-content">
        {/* MOBILE TOP BAR WITH CENTERED NAME */}
        <div className="mobile-top-bar d-md-none">
          <MenuIcon className="cursor-pointer" onClick={() => setIsSidebarOpen(true)} />
          <div className="brand-text">Dine Spark</div>
          <div style={{ width: 24 }}></div> {/* Balance spacer */}
        </div>

        <header className="reports-header-ui">
          <div className="title-group">
            <h1 className="serif-title">Reports</h1>
            <p className="subtitle-text">Sales reports and analytics</p>
          </div>
          
          <div className="header-controls-group">
            <div className="view-select-wrapper header-dropdown">
              <select value={viewMode} onChange={(e) => setViewMode(e.target.value)} className="heading-select">
                <option value="daily">Daily</option>
                <option value="weekly">Weekly</option>
                <option value="monthly">Monthly</option>
                <option value="yearly">Yearly</option>
              </select>
              <ChevronDown size={14} className="dropdown-arrow" />
            </div>
            <div className="date-picker-ui">
              <Calendar size={16} className="d-md-none" style={{marginRight: '8px'}} />
              <span>Date:</span>
              <input type="date" value={selectedDate} onChange={(e) => setSelectedDate(e.target.value)} />
            </div>
          </div>
        </header>

        <div className="stats-grid-ui">
          <div className="stat-card-ui">
            <div className="stat-icon-wrapper orange-tint"><IndianRupee size={22} color="#BA5028" /></div>
            <div className="stat-details"><p>Total Sales</p><h3>₹{reportData.totalSales.toLocaleString()}</h3></div>
          </div>
          <div className="stat-card-ui">
            <div className="stat-icon-wrapper green-tint"><Receipt size={22} color="#166534" /></div>
            <div className="stat-details"><p>GST Sales</p><h3>₹{reportData.gstSales.toLocaleString()}</h3></div>
          </div>
          <div className="stat-card-ui">
            <div className="stat-icon-wrapper brown-tint"><BarChart3 size={22} color="#57534E" /></div>
            <div className="stat-details"><p>Non-GST Sales</p><h3>₹{reportData.nonGstSales.toLocaleString()}</h3></div>
          </div>
        </div>

        <div className="transactions-label-mobile d-md-none">TRANSACTIONS</div>

        {/* DESKTOP TABLE VIEW */}
        <div className="report-table-box d-none d-md-block">
          <table className="ui-data-table">
            <thead>
              <tr>
                <th>BILL NO</th>
                <th>TABLE</th>
                <th>ITEMS</th>
                <th>AMOUNT</th>
                <th>TYPE</th>
                <th>DATE & TIME</th>
              </tr>
            </thead>
            <tbody>
              {reportData.orders.length > 0 ? (
                reportData.orders.map((order) => {
                  const [y, m, d] = order.sale_date.split("-");
                  return (
                    <tr key={order.id}>
                      <td className="bold-id" style={{ fontWeight: 'bold', color: '#BA5028' }}>
                        {order.displayId}
                      </td>
                      <td>{order.table_id === "Walk-in" ? "Walk-in" : `Table ${order.table_id}`}</td>
                      <td>{order.items_count} items</td>
                      <td className="bold-amount">₹{order.grand_total}</td>
                      <td>
                        <span className={parseFloat(order.gst_amount) > 0 ? "gst-badge-rust" : "gst-badge-muted"}>
                          {parseFloat(order.gst_amount) > 0 ? "GST" : "Non-GST"}
                        </span>
                      </td>
                      <td className="time-text">{`${d}-${m}-${y} | ${order.sale_time}`}</td>
                    </tr>
                  );
                })
              ) : (
                <tr><td colSpan="6" className="no-data-msg">No sales found for this period.</td></tr>
              )}
            </tbody>
          </table>
        </div>

        {/* MOBILE CARD VIEW (Replaces Table on small screens) */}
        <div className="mobile-reports-list d-md-none">
          {reportData.orders.length > 0 ? (
            reportData.orders.map((order) => {
              const [y, m, d] = order.sale_date.split("-");
              return (
                <div className="mobile-report-card" key={order.id}>
                  <div className="card-header">
                    <div className="bill-main">
                      <span className="mobile-label">BILL NO</span>
                      <span className="mobile-id">{order.displayId}</span>
                    </div>
                    <span className={parseFloat(order.gst_amount) > 0 ? "badge-gst" : "badge-nongst"}>
                      {parseFloat(order.gst_amount) > 0 ? "GST" : "Non-GST"}
                    </span>
                  </div>
                  
                  <div className="card-body">
                    <div className="info-item">
                      <span className="mobile-label">TABLE</span>
                      <span className="mobile-value">{order.table_id === "Walk-in" ? "Walk-in" : `Table ${order.table_id}`}</span>
                    </div>
                    <div className="info-item">
                      <span className="mobile-label">ITEMS</span>
                      <span className="mobile-value">{order.items_count} items</span>
                    </div>
                  </div>

                  <div className="card-footer">
                    <div className="info-item">
                      <span className="mobile-label">DATE & TIME</span>
                      <span className="mobile-time">{`${d}-${m}-${y} | ${order.sale_time}`}</span>
                    </div>
                    <div className="amount-section">
                      <span className="mobile-label">AMOUNT</span>
                      <span className="mobile-grand-total">₹{order.grand_total}</span>
                    </div>
                  </div>
                </div>
              );
            })
          ) : (
            <div className="no-data-msg">No sales found for this period.</div>
          )}
        </div>
      </div>
    </div>
  );
}

export default Reports;